import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import strokeData from "../../data/stroke.json";
import strokeHistoryFollowup from "../../data/strokeHistoryFollowup.json";
import styles from "./StrokeCheckNew.module.css";
import { ArrowBack } from "@mui/icons-material";
import { getUserId } from "../../utils/userId";
import { getUserResponse } from "../../utils/firebase";
import { doc, setDoc, Timestamp, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { v4 as uuidv4 } from 'uuid';

// Interfaces for questions
type OptionValue = number | boolean | string;

interface HistoryRiskFactor {
  keywordKey: string;
  descriptionKey: string;
}

interface HistoryRiskMapping {
  [key: string]: {
    riskPoint: number;
    riskFactor?: HistoryRiskFactor;
    followUp?: HistoryFollowUpQuestion[];
  };
}

interface HistoryFollowUpQuestion {
  id: string;
  type: string;
  textKey?: string;
  options: { optionTextKey: string; value: OptionValue }[];
  riskMapping: HistoryRiskMapping;
  followUp?: HistoryFollowUpQuestion[];
}

interface HistoryQuestion {
  id: string;
  type: string;
  questionTextKey?: string;
  textKey?: string;
  options: { optionTextKey: string; value: OptionValue }[];
  riskMapping: HistoryRiskMapping;
  followUp?: HistoryFollowUpQuestion[];
}

interface StrokeRiskFactor {
  keyword: string;
  description: string;
}

interface StrokeQuestion {
  id: string;
  type: string;
  questionTextKey: string;
  options: { optionTextKey: string; value: OptionValue }[];
  riskFactor: StrokeRiskFactor;
  risk_trigger_values: OptionValue[];
  followUpIfYes?: any[];
}

interface MedicalHistoryData {
  test_id: string;
  description: string;
  questions: HistoryQuestion[];
}

interface StrokeData {
  test_id: string;
  description: string;
  questions: StrokeQuestion[];
}

type Question = StrokeQuestion | HistoryQuestion | HistoryFollowUpQuestion;

interface Answer {
  questionId: string;
  optionId: string;
  value: OptionValue;
}

const StrokeCheckNew: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const optionsContainerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [userBioResponse, setUserBioResponse] = useState<Record<string, any>>({
    bio_q4: [],
  });

  const [testCompleted, setTestCompleted] = useState(false);
  const [savingToFirebase, setSavingToFirebase] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [hasPreviousHistory, setHasPreviousHistory] = useState(false);

  const isStrokeQuestion = (question: Question): question is StrokeQuestion =>
    "risk_trigger_values" in question;
  const isHistoryQuestion = (question: Question): question is HistoryQuestion =>
    "riskMapping" in question;

  const calculateTotalMaxRisk = (questions: any[]): number => {
    let count = 0;
    
    questions.forEach((q) => {
      let hasDirectRisk = false;
      let followUps: any[] = [];

      // 1. StrokeQuestion 타입인 경우
      if ('riskFactor' in q && q.riskFactor) {
        hasDirectRisk = true;
      }
      if ('followUpIfYes' in q && q.followUpIfYes) {
        followUps.push(...q.followUpIfYes);
      }

      // 2. HistoryQuestion 타입인 경우 (riskMapping 확인)
      if ('riskMapping' in q && q.riskMapping) {
        Object.values(q.riskMapping).forEach((mapping: any) => {
          if (mapping.riskFactor) hasDirectRisk = true;
          if (mapping.followUp) followUps.push(...mapping.followUp);
        });
      }

      // 이 질문 자체에서 위험요인이 발생할 수 있다면 +1
      if (hasDirectRisk) {
        count++;
      }

      // 꼬리 질문들 재귀 계산
      if (followUps.length > 0) {
        count += calculateTotalMaxRisk(followUps);
      }
    });

    return count;
  };

  useEffect(() => {
    if (testCompleted) {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [testCompleted]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const userId = getUserId();
        console.log("현재 로그인된 User ID:", userId);

        let bio_q4: string[] = [];

        if (userId) {
          // [수정] getUserResponse 대신 직접 'users' 컬렉션 조회
          const userDocRef = doc(db, 'users', userId);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            console.log("[디버그] Firestore 'users' 데이터:", userData);
            // MyInformation에서 저장한 구조: userData.responses.bio_q4
            bio_q4 = userData.responses?.bio_q4 || [];
          } else {
            console.log("[디버그] 'users' 컬렉션에 해당 유저 문서가 없습니다.");
          }

          const qPrevious = query(
            collection(db, "userStrokeResponse"),
            where("userId", "==", userId)
          );
          const querySnapshotPrevious = await getDocs(qPrevious);
          
          if (querySnapshotPrevious.docs.length > 0) {
            setHasPreviousHistory(true);
          }
        }

        setUserBioResponse({ bio_q4 });
        console.log("==========================================");
        console.log("[디버그] MyInformation (bio_q4)에 저장된 질환 키:", bio_q4);
        console.log("==========================================");

        const baseQuestions = [
          ...(strokeData as StrokeData).questions,
        ] as Question[];

        const diseasePrefixMap: { [key: string]: string } = {
          "hypertension": "hypertension",    // 고혈압
          "diabetes": "diabetes",            // 당뇨
          "high_cholesterol": "cholesterol", // 고지혈증 (JSON 파일은 cholesterol_... 로 시작함)
          "stroke": "strokeh"                // 뇌졸중 과거력 (JSON 파일은 strokeh_... 로 시작함)
        };

        const historyQuestions = (
          strokeHistoryFollowup as unknown as MedicalHistoryData
        ).questions.filter((q) =>
          bio_q4.some((bio: string) => {
            const prefix = diseasePrefixMap[bio]; 
            return prefix && q.id.startsWith(prefix);
          })
        );

        console.log("[디버그] 추가된 과거력 질문 개수:", historyQuestions.length);

        setAllQuestions([...baseQuestions, ...historyQuestions]);
      } catch (error) {
        console.error("Failed to load user data:", error);
        setAllQuestions([...(strokeData as StrokeData).questions]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    optionsContainerRefs.current = optionsContainerRefs.current.slice(
      0,
      allQuestions.length
    );
  }, [allQuestions.length]);

  useEffect(() => {
    if (
      chatContainerRef.current &&
      optionsContainerRefs.current[currentQuestionIndex]
    ) {
      optionsContainerRefs.current[currentQuestionIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
      console.log(getRiskFactors());
    }
  }, [currentQuestionIndex, answers]);

  const getProgressPercentage = (idx: number, totalCount: number) => {
    if (totalCount === 0) return 0;
    return Math.min(Math.round(((idx + 1) / totalCount) * 100), 100);
  };

  const updateAnswers = (newAnswer: Answer) => {
    const idx = answers.findIndex((a) => a.questionId === newAnswer.questionId);
    if (idx !== -1) {
      const updated = [...answers];
      updated[idx] = newAnswer;
      setAnswers(updated);
    } else {
      setAnswers([...answers, newAnswer]);
    }
  };

  const handleViewResults = async () => {
    setSavingToFirebase(true);
    try {
      const totalScore = answers.reduce(
        (sum, ans) => (typeof ans.value === "number" ? sum + ans.value : sum),
        0
      );
      const riskFactors = getRiskFactors();
      const qaRiskFactors = getQaRiskFactors();

      const BASE_COUNT = 23; // 기본 문항 수
      let diseaseCount = 0;

      // 개인 질환별 가중치 매핑
      const diseaseWeightMap: { [key: string]: number } = {
        "hypertension": 3,
        "diabetes": 5,
        "high_cholesterol": 2, // cholesterol 키 대응
        "stroke": 3           // strokeh 키 대응
      };

      // 사용자가 선택한 기저질환(bio_q4)을 돌며 가중치 합산
      userBioResponse.bio_q4.forEach((diseaseKey: string) => {
        if (diseaseWeightMap[diseaseKey]) {
          diseaseCount += diseaseWeightMap[diseaseKey];
        }
      });

      // 최종 합산: 기본(23) + 선택 질환 가중치
      const finalTotalQuestions = BASE_COUNT + diseaseCount;

      const sessionId = sessionStorage.getItem("current_session_id");
      
      // Save to Firebase
      const userId = getUserId();
      if (!userId) {
        console.error('No user ID found');
        setSavingToFirebase(false);
        return;
      }

      console.log(`[디버그] 계산된 총 위험 요인 개수: ${finalTotalQuestions}`);
      
      navigate(`${prefix}/stroke/check/results`, {
      state: { 
        score: totalScore, 
        answers, 
        riskFactors, 
        qaRiskFactors: qaRiskFactors,
        totalQuestions: finalTotalQuestions,
        allQuestions: allQuestions,
        sessionId: sessionId
      },
    });
    } catch (error) {
      console.error('Error navigating to results:', error);
    } finally {
      setSavingToFirebase(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedOption) return;

    const currentQuestion = allQuestions[currentQuestionIndex];
    const selectedOptionData = currentQuestion.options.find(
      (opt) => opt.optionTextKey === selectedOption
    );
    if (!selectedOptionData) return;

    const newAnswer = {
      questionId: currentQuestion.id,
      optionId: selectedOption,
      value: selectedOptionData.value,
    };
    updateAnswers(newAnswer);

    // Handle follow-up injection
    let hasAddedFollowUp = false;
    if (isHistoryQuestion(currentQuestion)) {
      const followUp =
        currentQuestion.riskMapping[String(selectedOptionData.value)]
          ?.followUp || currentQuestion.followUp;
      if (followUp && followUp.length > 0) {
        const updatedQuestions = [...allQuestions];
        updatedQuestions.splice(currentQuestionIndex + 1, 0, ...followUp);
        setAllQuestions(updatedQuestions);
        hasAddedFollowUp = true;
      }
    }

    if (isStrokeQuestion(currentQuestion) && selectedOptionData.value === true) {
      if (currentQuestion.followUpIfYes && currentQuestion.followUpIfYes.length > 0) {
        
        const followUpQuestions = currentQuestion.followUpIfYes.map((q: any) => ({
          ...q,
          type: q.type || 'yes_no_followup',
        }));
        
        const updatedQuestions = [...allQuestions];
        updatedQuestions.splice(currentQuestionIndex + 1, 0, ...followUpQuestions);
        setAllQuestions(updatedQuestions);
        hasAddedFollowUp = true;
      }
    }

    setSelectedOption(null);

    if (isEditing) {
      handleBackToLatest();
    } else if (hasAddedFollowUp || currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setTestCompleted(true);
    }
  };

  const getRiskFactors = () => {
  const factors: { keyword: string; description: string }[] = [];

  answers.forEach((answer) => {
    const question = allQuestions.find((q) => q.id === answer.questionId);
    if (!question) {
      console.warn(
        `[RiskFactor] Answer ${answer.questionId} 에 해당하는 질문을 못 찾음`
      );
      return;
    }

    // [⭐️ 원래 로직] Stroke 질문 타입
    if (isStrokeQuestion(question)) {
      const isRisk = question.risk_trigger_values.includes(answer.value);
      if (isRisk) {
        console.log(
          `[RiskFactor] [Stroke] ${question.id}: value=${answer.value} → keyword=${question.riskFactor.keyword}`
        );
        factors.push({
          keyword: question.riskFactor.keyword, // (원래 로직)
          description: question.riskFactor.description, // (원래 로직)
        });
      } else {
        console.log(
          `[RiskFactor] [Stroke] ${question.id}: value=${answer.value} → No Risk`
        );
      }
    }

    // [⭐️ 원래 로직] History 질문 타입
    if (isHistoryQuestion(question)) {
      const riskMappingEntry = question.riskMapping[String(answer.value)];
      if (riskMappingEntry) {
        if (riskMappingEntry.riskFactor) {
          console.log(
            `[RiskFactor] [History] ${question.id}: value=${answer.value} → keyword=${riskMappingEntry.riskFactor.keywordKey}`
          );
          factors.push({
            keyword: t(riskMappingEntry.riskFactor.keywordKey), // (원래 로직)
            description: t(riskMappingEntry.riskFactor.descriptionKey), // (원래 로직)
          });
        } else {
          console.log(
            `[RiskFactor] [History] ${question.id}: value=${answer.value} → No RiskFactor`
          );
        }
      } else {
        console.log(
          `[RiskFactor] [History] ${question.id}: value=${answer.value} → No Mapping`
        );
      }
    }
  });

  // 중복 제거 (keyword 기준)
  return factors.filter(
    (factor, index, self) =>
      index === self.findIndex((f) => f.keyword === factor.keyword)
  );
};

const getQaRiskFactors = () => {
  const factors: { keyword: string; description: string }[] = [];

  answers.forEach((answer) => {
    const question = allQuestions.find((q) => q.id === answer.questionId);
    if (!question) return;

    // Stroke 질문 타입
    if (isStrokeQuestion(question)) {
      const isRisk = question.risk_trigger_values.includes(answer.value);
      if (isRisk) {
        factors.push({
          keyword: getQuestionText(question), // (질문)
          description: t(answer.optionId), // (답변)
        });
      }
    }

    // History 질문 타입
    if (isHistoryQuestion(question)) {
      const riskMappingEntry = question.riskMapping[String(answer.value)];
      if (riskMappingEntry && riskMappingEntry.riskFactor) {
        factors.push({
          keyword: getQuestionText(question), // (질문)
          description: t(answer.optionId), // (답변)
        });
      }
    }
  });

  // 중복 제거
  return factors.filter(
    (factor, index, self) =>
      index === self.findIndex((f) => 
        f.keyword === factor.keyword && f.description === factor.description
      )
  );
};

  const handleBack = () => {
    const checkPagePath = `${prefix}/stroke_check`; // 기록이 있을 때 돌아갈 페이지
    const listPagePath = `${prefix}/check`;         // 기록이 없을 때 돌아갈 'Check' 페이지

    // 기록이 있다고 판단하는 조건:
    // 1. 현재 답변을 한 상태 (answers.length > 0) OR
    // 2. 이전에 완료된 테스트 기록이 있을 때 (hasPreviousHistory)
    const shouldGoToCheckPage = answers.length > 0 || hasPreviousHistory;

    if (shouldGoToCheckPage) {
      navigate(checkPagePath);
    } else {
      navigate(listPagePath, { replace: true });
    }
  };

  const handleBackToLatest = () => {
    setCurrentQuestionIndex(allQuestions.length - 1);
    setIsEditing(false);
  };

  const setOptionRef = (index: number) => (el: HTMLDivElement | null) => {
    optionsContainerRefs.current[index] = el;
  };

  const getQuestionText = (question: any): string => {
    const textKey = question.questionTextKey || question.textKey;
    return textKey ? t(textKey as string) : '';
};

  if (isLoading) return <div className={styles.loading}>Loading...</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t("check_before_2")}</span>
      </div>
      <div className={styles.chatContainer} ref={chatContainerRef}>
        {allQuestions.map((q, idx) => {
          const selectedAnswer = answers.find((a) => a.questionId === q.id);
          const isCurrent = idx === currentQuestionIndex && !testCompleted;
          
          // 진행률 계산
          const progress = getProgressPercentage(idx, allQuestions.length);

          if (idx > currentQuestionIndex && !selectedAnswer) return null;

          return (
            <React.Fragment key={q.id}>
              <div
                className={`${styles.botMessage} ${
                  !isCurrent ? styles.blurred : ""
                }`}
              >
                <div className={styles.messageContent}>
                  {/* 진행률 바와 텍스트 추가 */}
                  <div className={styles.progressContainer}>
                    <div className={styles.progressBarBackground}>
                      <div 
                        className={styles.progressBar} 
                        style={{ width: `${progress}%` }} 
                      />
                    </div>
                    <span className={styles.progressText}>{progress}%</span>
                  </div>

                  <div className={styles.questionText}>
                    {getQuestionText(q as HistoryQuestion | HistoryFollowUpQuestion)}
                  </div>
                </div>
              </div>
              
              {/* 유저 메시지 부분 (기존과 동일하지만 구조 유지를 위해 포함) */}
              <div
                className={`${styles.userMessage} ${
                  !isCurrent ? styles.blurred : ""
                }`}
              >
                {isCurrent ? (
                  <div ref={setOptionRef(idx)} className={styles.userResponseWrapper}>
                    <div className={styles.optionsContainer}>
                      {q.options.map((opt) => (
                        <button
                          key={opt.optionTextKey}
                          className={`${styles.optionButton} ${
                            selectedOption === opt.optionTextKey ? styles.selected : ""
                          }`}
                          onClick={() => setSelectedOption(opt.optionTextKey)}
                        >
                          {t(opt.optionTextKey)}
                        </button>
                      ))}
                    </div>
                    <button
                      className={styles.confirmButton}
                      onClick={handleConfirm}
                      disabled={!selectedOption}
                    >
                      {t("confirm_button_text")}
                    </button>
                  </div>
                ) : (
                  selectedAnswer && (
                    <div
                      className={styles.selectedAnswer}
                      onClick={() => setCurrentQuestionIndex(idx)}
                    >
                      {t(selectedAnswer.optionId)}
                    </div>
                  )
                )}
              </div>
            </React.Fragment>
          );
        })}
        {testCompleted && (
          <div ref={resultsRef}>
            <div className={styles.botMessage}>
              <div className={styles.messageContent}>
                <div className={styles.questionText}>{t("test_result")}</div>
              </div>
            </div>
            <div className={styles.botMessage}>
              <button
                className={styles.viewResultsButton}
                onClick={handleViewResults}
                disabled={savingToFirebase}
              >
                {savingToFirebase ? t("saving") : t("view_results")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrokeCheckNew;
 